import React, { useCallback, useMemo, useRef } from 'react'
import { View } from '@/components'
import { createStyles } from '@codeleap/styles'
import { Chat, textVariant } from '@/config'
import {
  usePollContext,
  useStateStore,
  useChannelStateContext,
  useTranslationContext,
} from 'stream-chat-react'
import type { PollOption, PollVote } from 'stream-chat'
import { isVoteAnswer } from 'stream-chat'
import { PollOptionRow } from './PollOptionRow'
import { ChatLocalMesage } from '@packages/chat/src'
import { useToggling } from '@/hooks'

const MAX_VISIBLE_AVATARS = 3

export const PollOptionList = ({ message, isThreadParent: _isThreadParent }: { message: ChatLocalMesage; isThreadParent: boolean }) => {
  const { poll } = usePollContext()
  const { t } = useTranslationContext()
  const mountedRef = useRef(true)
  const { withToggling } = useToggling()

  const {
    options,
    voteCounts,
    ownVotes,
    latestVotes,
    maxVotedOptionIds,
    votingVisibility,
    isClosed,
  } = useStateStore(poll.state, state => ({
    options: (state.options || []) as PollOption[],
    voteCounts: state.vote_counts_by_option || {},
    ownVotes: state.ownVotesByOptionId || {},
    latestVotes: state.latest_votes_by_option || {},
    maxVotedOptionIds: state.maxVotedOptionIds || [],
    votingVisibility: state.voting_visibility,
    isClosed: state.is_closed,
  }))

  const chatUser = Chat.Store.useUser()
  const isMyMessage = message?.user?.id === chatUser?.id
  const messageId = message?.id

  const { channelCapabilities = {} } = useChannelStateContext('PollOptionList')
  const canCastVote = channelCapabilities['cast-poll-vote'] && !isClosed

  const maxVotes = useMemo(
    () => Math.max(...Object.values(voteCounts || {}).map(v => (typeof v === 'number' ? v : 0)), 0),
    [voteCounts],
  )

  const hasSingleWinner = isClosed && maxVotedOptionIds?.length === 1

  const optionsData = useMemo(() => {
    return options.map(option => {
      const votes = typeof voteCounts?.[option.id] === 'number' ? voteCounts?.[option.id] : 0
      const hasMyVote = !!ownVotes?.[option.id]
      const isWinner = hasSingleWinner && maxVotedOptionIds?.[0] === option.id

      const progressBase = maxVotes || votes
      const progress = progressBase ? (votes / progressBase) * 100 : votes > 0 ? 100 : 0

      const relevantVotes: PollVote[] =
        latestVotes?.[option.id]?.filter(vote => !isVoteAnswer(vote)).slice(0, MAX_VISIBLE_AVATARS) ?? []

      return {
        option,
        votes,
        hasMyVote,
        isWinner,
        progress,
        relevantVotes,
      }
    })
  }, [hasSingleWinner, latestVotes, maxVotedOptionIds, maxVotes, options, ownVotes, voteCounts])

  const handleToggleVote = useCallback((optionId: string) =>
    withToggling(optionId, async () => {
      if (!canCastVote || !messageId) return

      const currentVote = ownVotes?.[optionId]

      try {
        if (currentVote) {
          await poll.removeVote(currentVote.id, messageId)
        } else {
          await poll.castVote(optionId, messageId)
        }
      } catch (error) {
        console.error('Failed to toggle vote', error)

        if (mountedRef.current) {
          const title = t?.('Error') || 'Error'
          const body =
            t?.('Failed to register vote. Please try again.') ||
            'Failed to register vote. Please try again.'
          // Using alert from modals is heavier; keep it minimal here for performance
          // eslint-disable-next-line no-alert
          window.alert(`${title}\n${body}`)
        }
      }
    }),
    [canCastVote, messageId, ownVotes, poll, t, withToggling])

  React.useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  return (
    <View style={styles.optionsWrapper}>
      {optionsData.map(optionData => (
        <PollOptionRow
          key={optionData.option.id}
          option={optionData.option}
          votes={optionData.votes}
          hasMyVote={optionData.hasMyVote}
          isWinner={optionData.isWinner}
          progress={optionData.progress}
          relevantVotes={optionData.relevantVotes}
          canCastVote={canCastVote}
          votingVisibility={votingVisibility}
          onToggleVote={handleToggleVote}
          isMyMessage={isMyMessage}
        />
      ))}
    </View>
  )
}

const styles = createStyles(theme => ({
  container: {
    ...theme.presets.column,
    ...theme.spacing.gap(1.5),
    ...theme.spacing.padding(1.5),
    borderRadius: theme.radius.regular,
    backgroundColor: theme.colors.bgDefault,
  },
  containerMine: {
    backgroundColor: theme.baseColors.primarySolid500,
  },
  header: {
    ...theme.presets.column,
    ...theme.spacing.gap(0.25),
  },
  title: {
    ...textVariant('p3'),
    fontWeight: 600,
    color: theme.colors.textHeadingRegular,
  },
  titleMine: {
    color: theme.baseColors.neutralSolid0,
  },
  subtitle: {
    ...textVariant('c3'),
    color: theme.colors.textCaption,
  },
  subtitleMine: {
    color: theme.baseColors.neutralSolid200,
  },
  optionsWrapper: {
    ...theme.presets.column,
    ...theme.spacing.gap(1),
  },
  actions: {
    ...theme.presets.row,
    ...theme.spacing.gap(1),
  },
  actionButton: {
    touchableWrapper: {
      minWidth: theme.spacing.value(10),
    },
  },
  optionRow: {
    ...theme.presets.row,
    ...theme.presets.alignStart,
    ...theme.spacing.gap(1),
    ...theme.spacing.padding(1),
    borderRadius: theme.radius.regular,
    backgroundColor: theme.baseColors.neutralBlackTransparent50,
    cursor: 'pointer',
  },
  optionRowMine: {
    backgroundColor: theme.baseColors.primaryTransparent100,
  },
  optionRowDisabled: {
    cursor: 'default',
    opacity: 0.6,
  },
  optionLeading: {
    ...theme.presets.center,
    height: theme.spacing.value(3),
  },
  checkbox: {
    ...theme.presets.center,
    ...theme.sized(2.5),
    borderRadius: theme.radius.small,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.baseColors.primarySolid500,
    backgroundColor: 'transparent',
  },
  checkboxMine: {
    borderColor: theme.baseColors.neutralSolid0,
  },
  checkboxChecked: {
    backgroundColor: theme.baseColors.primarySolid500,
  },
  checkboxCheckedMine: {
    backgroundColor: theme.baseColors.neutralSolid0,
  },
  checkboxIcon: {
    color: theme.baseColors.neutralSolid0,
    ...theme.sized(1.5),
  },
  checkboxIconMine: {
    color: theme.baseColors.primarySolid500,
  },
  optionContent: {
    ...theme.presets.column,
    ...theme.spacing.gap(0.5),
    flex: 1,
  },
  optionHeader: {
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    ...theme.presets.justifySpaceBetween,
    ...theme.spacing.gap(1),
  },
  optionText: {
    ...textVariant('p3'),
    color: theme.colors.textHeadingRegular,
  },
  optionTextMine: {
    color: theme.baseColors.neutralSolid0,
  },
  voteCount: {
    ...textVariant('c3'),
    color: theme.colors.textCaption,
  },
  voteCountMine: {
    color: theme.baseColors.neutralSolid0,
  },
  progressBar: {
    position: 'relative',
    width: '100%',
    height: theme.spacing.value(0.75),
    borderRadius: theme.radius.small,
    overflow: 'hidden',
    backgroundColor: theme.baseColors.neutralBlackTransparent100,
  },
  progressBarMine: {
    backgroundColor: theme.baseColors.neutralWhiteTransparent250,
  },
  progressFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    backgroundColor: theme.baseColors.primarySolid500,
  },
  progressFillMine: {
    backgroundColor: theme.baseColors.neutralSolid0,
  },
  progressFillWinner: {
    backgroundColor: theme.baseColors.primarySolid700,
  },
  avatars: {
    ...theme.presets.row,
    ...theme.spacing.gap(0.5),
  },
}))
